import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import {
  listDevices, createDevice, createEnrollmentToken, listOrganizations, listReleases,
  createBulkOperation, listBulkOperations, cancelBulkOperation,
  approveDevice, rejectDevice,
  listEnrollmentCodes, createEnrollmentCode,
  Device, Organization, Release, BulkOperation, EnrollmentCode,
} from '../api/client'
import StatusBadge from '../components/StatusBadge'
import { useAuth } from '../auth/AuthContext'

function formatLastSeen(unix: number): string {
  if (!unix) return 'Never'
  const diff = Math.floor(Date.now() / 1000) - unix
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(unix * 1000).toLocaleDateString()
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #e5e7eb',
  background: '#f9fafb',
}

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid #f3f4f6',
  fontSize: 14,
}

// AddDeviceModal defaults to surfacing the self-registration enrollment
// code for the chosen org — the primary way devices get added now that
// field techs enter a code into the device's own local setup page. The
// old pre-provisioned-token flow (manually assigning a device ID and
// minting a one-time token) is still available as an "advanced" fallback
// for scripted/manual provisioning, tucked behind showManual.
interface AddDeviceModal {
  open: boolean
  orgID: number | ''
  showManual: boolean
  newCodeName: string
  // manual/pre-provisioned-token fields
  id: string
  name: string
  site: string
  token: string | null
  error: string | null
}

const emptyModal: AddDeviceModal = {
  open: false, orgID: '', showManual: false, newCodeName: '',
  id: '', name: '', site: '', token: null, error: null,
}

interface BulkActionModal {
  // Reboot always uses updateType 'reboot' with no dropdown shown; the
  // "Push Update" entry point shows the full type picker.
  mode: 'reboot' | 'update'
  updateType: BulkOperation['UpdateType']
  releaseVersion: string
  packageSpec: string
  paceSeconds: number
  error: string | null
}

export default function DeviceList() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data: devices = [], isLoading, error } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: () => listDevices(),
    refetchInterval: 10_000,
  })
  // listOrganizations already returns every org the user can see, including
  // their own — multi-org users (resellers with sub-orgs, or super users)
  // need to pick which one to get the right enrollment code for.
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })

  const pendingDevices = devices.filter(d => d.Status === 'pending_approval')
  const allActiveDevices = devices.filter(d => d.Status !== 'pending_approval')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [orgFilter, setOrgFilter] = useState<number | ''>('')
  const searchLower = search.trim().toLowerCase()
  const activeDevices = allActiveDevices.filter(d => {
    if (statusFilter && d.Status !== statusFilter) return false
    if (orgFilter !== '' && d.OrgID !== orgFilter) return false
    if (!searchLower) return true
    return (
      d.ID.toLowerCase().includes(searchLower) ||
      d.Name.toLowerCase().includes(searchLower) ||
      d.Site.toLowerCase().includes(searchLower)
    )
  })
  const activeStatuses = Array.from(new Set(allActiveDevices.map(d => d.Status))).sort()
  const orgNameByID = new Map(orgs.map(o => [o.ID, o.Name]))

  const [approveModal, setApproveModal] = useState<{ device: Device; name: string; site: string } | null>(null)
  const approveMut = useMutation({
    mutationFn: (vars: { deviceId: string; name: string; site: string }) =>
      approveDevice(vars.deviceId, { name: vars.name, site: vars.site }),
    onSuccess: () => {
      setApproveModal(null)
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
  })
  const rejectMut = useMutation({
    mutationFn: (deviceId: string) => rejectDevice(deviceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  })

  const [modal, setModal] = useState<AddDeviceModal>(emptyModal)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkModal, setBulkModal] = useState<BulkActionModal | null>(null)

  const openAddModal = () => setModal({ ...emptyModal, open: true, orgID: user?.OrgID ?? '' })

  const { data: enrollmentCodes = [] } = useQuery<EnrollmentCode[]>({
    queryKey: ['enrollment-codes'],
    queryFn: listEnrollmentCodes,
    enabled: modal.open && !modal.showManual,
  })
  const activeCodesForOrg = enrollmentCodes.filter(c => !c.RevokedAt && c.OrgID === modal.orgID)
  const canManageDevices = !!(user?.IsSuperUser || user?.CanManageDevices)

  const createCodeMutation = useMutation({
    mutationFn: () => createEnrollmentCode(modal.newCodeName.trim(), modal.orgID === '' ? undefined : modal.orgID),
    onSuccess: () => {
      setModal(m => ({ ...m, newCodeName: '' }))
      qc.invalidateQueries({ queryKey: ['enrollment-codes'] })
    },
  })

  const toggleSelected = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const allSelected = activeDevices.length > 0 && selected.size === activeDevices.length
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(activeDevices.map(d => d.ID)))

  const createMutation = useMutation({
    mutationFn: async () => {
      await createDevice({
        ID: modal.id.trim(),
        Name: modal.name.trim(),
        Site: modal.site.trim(),
        OrgID: modal.orgID === '' ? undefined : modal.orgID,
      })
      const token = await createEnrollmentToken(modal.id.trim())
      return token
    },
    onSuccess: (token) => {
      setModal((m) => ({ ...m, token, error: null }))
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      setModal((m) => ({ ...m, error: msg }))
    },
  })

  const closeModal = () => setModal(emptyModal)

  const { data: releases = [] } = useQuery<Release[]>({
    queryKey: ['releases'],
    queryFn: listReleases,
    enabled: bulkModal?.mode === 'update',
  })
  const { data: bulkOps = [] } = useQuery<BulkOperation[]>({
    queryKey: ['bulk-operations'],
    queryFn: () => listBulkOperations(20),
    refetchInterval: (query) => (query.state.data?.some(op => op.Status === 'running') ? 4_000 : 15_000),
  })

  const bulkMutation = useMutation({
    mutationFn: async () => {
      if (!bulkModal) throw new Error('no bulk action in progress')
      const rel = releases.find(r => r.Version === bulkModal.releaseVersion)
      return createBulkOperation({
        deviceIDs: Array.from(selected),
        updateType: bulkModal.updateType,
        releaseVersion: rel?.Version,
        downloadUrl: rel?.DownloadURL,
        sha256: rel?.SHA256,
        packageSpec: bulkModal.updateType === 'package' ? bulkModal.packageSpec : undefined,
        paceSeconds: bulkModal.paceSeconds,
      })
    },
    onSuccess: () => {
      setBulkModal(null)
      setSelected(new Set())
      qc.invalidateQueries({ queryKey: ['bulk-operations'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      setBulkModal(m => m && ({ ...m, error: msg }))
    },
  })

  const cancelBulkMutation = useMutation({
    mutationFn: (id: number) => cancelBulkOperation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bulk-operations'] }),
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Device Fleet</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {activeDevices.length === allActiveDevices.length
              ? `${activeDevices.length} device${activeDevices.length !== 1 ? 's' : ''}`
              : `${activeDevices.length} of ${allActiveDevices.length} devices`} · auto-refreshes every 10s
          </span>
          <button
            onClick={openAddModal}
            style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            + Add Device
          </button>
        </div>
      </div>

      {pendingDevices.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>
            Pending Approval ({pendingDevices.length})
          </h3>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name / Device ID</th>
                  <th style={thStyle}>MAC</th>
                  <th style={thStyle}>Source IP</th>
                  <th style={thStyle}>Requested</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pendingDevices.map(d => (
                  <tr key={d.ID}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{d.Name || <em style={{ color: '#9ca3af' }}>no name given</em>}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{d.ID}</div>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12.5 }}>{d.EnrollmentMAC || '—'}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12.5 }}>{d.EnrollmentIP || '—'}</td>
                    <td style={{ ...tdStyle, color: '#6b7280', fontSize: 13 }}>
                      {d.EnrollmentRequestedAt ? formatLastSeen(Math.floor(new Date(d.EnrollmentRequestedAt).getTime() / 1000)) : '—'}
                    </td>
                    <td style={{ ...tdStyle, display: 'flex', gap: 8 }}>
                      <button
                        style={{ ...bulkBtnStyle, background: '#2563eb', color: '#fff' }}
                        onClick={() => setApproveModal({ device: d, name: d.Name, site: '' })}
                      >
                        Approve
                      </button>
                      <button
                        style={{ ...bulkBtnStyle, background: '#fee2e2', color: '#991b1b' }}
                        onClick={() => { if (window.confirm(`Reject device ${d.Name || d.ID}? This cannot be undone.`)) rejectMut.mutate(d.ID) }}
                        disabled={rejectMut.isPending}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 16px',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
            {selected.size} device{selected.size !== 1 ? 's' : ''} selected
          </span>
          <button
            style={{ ...bulkBtnStyle, background: '#fff', border: '1px solid #d1d5db', color: '#374151' }}
            onClick={() => setBulkModal({ mode: 'reboot', updateType: 'reboot', releaseVersion: '', packageSpec: '', paceSeconds: 30, error: null })}
          >
            Reboot Selected
          </button>
          <button
            style={{ ...bulkBtnStyle, background: '#fff', border: '1px solid #d1d5db', color: '#374151' }}
            onClick={() => setBulkModal({ mode: 'update', updateType: 'agent', releaseVersion: '', packageSpec: '', paceSeconds: 30, error: null })}
          >
            Push Update to Selected
          </button>
          <button
            style={{ ...bulkBtnStyle, background: 'none', border: 'none', color: '#6b7280' }}
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          style={{ flex: 1, maxWidth: 320, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by name, ID, or site…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {activeStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {orgs.length > 1 && (
          <select
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">All organizations</option>
            {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
          </select>
        )}
        {(search || statusFilter || orgFilter !== '') && (
          <button
            style={{ ...bulkBtnStyle, background: 'none', border: 'none', color: '#6b7280' }}
            onClick={() => { setSearch(''); setStatusFilter(''); setOrgFilter('') }}
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading && <p style={{ color: '#6b7280' }}>Loading devices...</p>}
      {error && <p style={{ color: '#dc2626' }}>Error loading devices: {String(error)}</p>}

      {!isLoading && !error && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th style={thStyle}>Device</th>
                <th style={thStyle}>Site</th>
                <th style={thStyle}>Organization</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Version</th>
                <th style={thStyle}>Registrations</th>
                <th style={thStyle}>CPU</th>
                <th style={thStyle}>Memory</th>
                <th style={thStyle}>Calls</th>
                <th style={thStyle}>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {activeDevices.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 32 }}>
                    {allActiveDevices.length === 0 ? 'No devices registered yet.' : 'No devices match your filters.'}
                  </td>
                </tr>
              )}
              {activeDevices.map((d) => (
                <tr key={d.ID} style={{ cursor: 'pointer' }}>
                  <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(d.ID)} onChange={() => toggleSelected(d.ID)} />
                  </td>
                  <td style={tdStyle}>
                    <Link
                      to={`/devices/${d.ID}`}
                      style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {d.Name || d.ID}
                    </Link>
                    {d.Name && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{d.ID}</div>
                    )}
                  </td>
                  <td style={tdStyle}>{d.Site || '—'}</td>
                  <td style={tdStyle}>{d.OrgID != null ? (orgNameByID.get(d.OrgID) ?? '—') : '—'}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={d.Disabled ? 'disabled' : d.Status} />
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                    {d.CurrentVersion || '—'}
                  </td>
                  <td style={tdStyle}>
                    {d.LastHealth ? d.LastHealth.RegisteredEndpoints : '—'}
                  </td>
                  <td style={tdStyle}>
                    {d.LastHealth ? `${d.LastHealth.CPUPercent.toFixed(1)}%` : '—'}
                  </td>
                  <td style={tdStyle}>
                    {d.LastHealth ? `${d.LastHealth.MemoryPercent.toFixed(1)}%` : '—'}
                  </td>
                  <td style={tdStyle}>
                    {d.LastHealth ? d.LastHealth.ActiveCalls : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: '#6b7280', fontSize: 13 }}>
                    {formatLastSeen(d.LastSeenUnix)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 28, width: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {modal.token ? (
              <>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Device Created</h2>
                <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                  Copy this enrollment token and paste it into <code>/etc/sipfd/config.yaml</code> on
                  the device. It expires in 24 hours and can only be used once.
                </p>
                <div style={{
                  background: '#f3f4f6', borderRadius: 6, padding: '10px 14px',
                  fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all',
                  border: '1px solid #e5e7eb', marginBottom: 20,
                }}>
                  {modal.token}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(modal.token!)}
                  style={{
                    background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6,
                    padding: '7px 14px', fontSize: 13, cursor: 'pointer', marginRight: 10,
                  }}
                >
                  Copy Token
                </button>
                <button
                  onClick={closeModal}
                  style={{
                    background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </>
            ) : modal.showManual ? (
              <>
                <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Pre-Provision a Device</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                  Assigns a specific device ID and mints a one-time enrollment token — for scripted
                  provisioning or when a device can't reach its own local setup page.
                </p>
                {modal.error && (
                  <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{modal.error}</p>
                )}
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Device ID <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  style={inputStyle}
                  placeholder="site-a-failover-01"
                  value={modal.id}
                  onChange={(e) => setModal((m) => ({ ...m, id: e.target.value }))}
                />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>
                  Display Name
                </label>
                <input
                  style={inputStyle}
                  placeholder="Site A Failover"
                  value={modal.name}
                  onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))}
                />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>
                  Site
                </label>
                <input
                  style={inputStyle}
                  placeholder="New York HQ"
                  value={modal.site}
                  onChange={(e) => setModal((m) => ({ ...m, site: e.target.value }))}
                />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>
                  Organization
                </label>
                <select
                  style={inputStyle}
                  value={modal.orgID}
                  onChange={(e) => setModal((m) => ({ ...m, orgID: e.target.value === '' ? '' : Number(e.target.value) }))}
                >
                  {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
                </select>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                  <a href="#" style={{ fontSize: 12.5 }} onClick={(e) => { e.preventDefault(); setModal(m => ({ ...m, showManual: false, error: null })) }}>
                    ← Back to enrollment code
                  </a>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={closeModal}
                      style={{
                        background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6,
                        padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => createMutation.mutate()}
                      disabled={!modal.id.trim() || createMutation.isPending}
                      style={{
                        background: modal.id.trim() ? '#2563eb' : '#93c5fd',
                        color: '#fff', border: 'none', borderRadius: 6,
                        padding: '8px 16px', fontSize: 13, fontWeight: 600,
                        cursor: modal.id.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {createMutation.isPending ? 'Creating…' : 'Create & Get Token'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Add Device</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                  Give a field tech this code to enter on the device's own local setup page — no
                  need to pre-create the device or assign it an ID. It shows up here under
                  "Pending Approval" once it registers.
                </p>
                {orgs.length > 1 && (
                  <>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      Organization
                    </label>
                    <select
                      style={inputStyle}
                      value={modal.orgID}
                      onChange={(e) => setModal((m) => ({ ...m, orgID: e.target.value === '' ? '' : Number(e.target.value) }))}
                    >
                      {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
                    </select>
                  </>
                )}

                {activeCodesForOrg.length > 0 ? (
                  <div style={{ marginTop: orgs.length > 1 ? 14 : 0 }}>
                    {activeCodesForOrg.map(c => (
                      <div key={c.ID} style={{ marginBottom: 10 }}>
                        {c.Name && <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 4px' }}>{c.Name}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            flex: 1, background: '#f3f4f6', borderRadius: 6, padding: '10px 14px',
                            fontFamily: 'monospace', fontSize: 18, letterSpacing: 1,
                            border: '1px solid #e5e7eb',
                          }}>
                            {c.Code}
                          </div>
                          <button
                            onClick={() => navigator.clipboard.writeText(c.Code)}
                            style={{
                              background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6,
                              padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : canManageDevices ? (
                  <div style={{ marginTop: orgs.length > 1 ? 14 : 0 }}>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>
                      No active enrollment code for this org yet.
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        style={{ ...inputStyle, marginBottom: 0 }}
                        placeholder="Label (e.g. Field techs Q3 2026)"
                        value={modal.newCodeName}
                        onChange={(e) => setModal(m => ({ ...m, newCodeName: e.target.value }))}
                      />
                      <button
                        onClick={() => createCodeMutation.mutate()}
                        disabled={createCodeMutation.isPending}
                        style={{
                          background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
                          padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        {createCodeMutation.isPending ? 'Creating…' : 'Create Code'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: orgs.length > 1 ? 14 : 0 }}>
                    No active enrollment code for this org yet — ask an admin to create one.
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                  {user?.IsSuperUser ? (
                    <a href="#" style={{ fontSize: 12.5 }} onClick={(e) => { e.preventDefault(); setModal(m => ({ ...m, showManual: true, error: null })) }}>
                      Advanced: pre-provision a device instead
                    </a>
                  ) : <span />}
                  <button
                    onClick={closeModal}
                    style={{
                      background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6,
                      padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {approveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 28, width: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Approve Device</h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
              Device ID: <code>{approveModal.device.ID}</code>
            </p>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Display Name</label>
            <input
              style={inputStyle}
              value={approveModal.name}
              onChange={(e) => setApproveModal(m => m && ({ ...m, name: e.target.value }))}
            />
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>Site</label>
            <input
              style={inputStyle}
              placeholder="New York HQ"
              value={approveModal.site}
              onChange={(e) => setApproveModal(m => m && ({ ...m, site: e.target.value }))}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setApproveModal(null)}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => approveMut.mutate({ deviceId: approveModal.device.ID, name: approveModal.name, site: approveModal.site })}
                disabled={approveMut.isPending}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {approveMut.isPending ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 28, width: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>
              {bulkModal.mode === 'reboot' ? 'Reboot' : 'Push Update to'} {selected.size} Device{selected.size !== 1 ? 's' : ''}
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
              Dispatched one device at a time, waiting the pace interval between each — not all at
              once. Progress continues on the server even if you close this tab.
            </p>
            {bulkModal.error && (
              <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{bulkModal.error}</p>
            )}

            {bulkModal.mode === 'update' && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Update Type</label>
                <select
                  style={inputStyle}
                  value={bulkModal.updateType}
                  onChange={(e) => setBulkModal(m => m && ({ ...m, updateType: e.target.value as BulkOperation['UpdateType'], releaseVersion: '', packageSpec: '' }))}
                >
                  <option value="agent">Agent Binary</option>
                  <option value="package">APT Package</option>
                  <option value="os">OS Upgrade (dist-upgrade)</option>
                </select>
                {bulkModal.updateType === 'agent' && (
                  <>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>Release</label>
                    <select
                      style={inputStyle}
                      value={bulkModal.releaseVersion}
                      onChange={(e) => setBulkModal(m => m && ({ ...m, releaseVersion: e.target.value }))}
                    >
                      <option value="">— select release —</option>
                      {releases.map(r => (
                        <option key={r.Version} value={r.Version}>{r.Version}{r.IsLatest ? ' (latest)' : ''}</option>
                      ))}
                    </select>
                  </>
                )}
                {bulkModal.updateType === 'package' && (
                  <>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>Package Spec</label>
                    <input
                      style={inputStyle}
                      placeholder="e.g. kamailio=5.8.1 or kamailio"
                      value={bulkModal.packageSpec}
                      onChange={(e) => setBulkModal(m => m && ({ ...m, packageSpec: e.target.value }))}
                    />
                  </>
                )}
              </>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 14 }}>
              Pace (seconds between each device)
            </label>
            <input
              type="number"
              min={5}
              style={inputStyle}
              value={bulkModal.paceSeconds}
              onChange={(e) => setBulkModal(m => m && ({ ...m, paceSeconds: Number(e.target.value) || 5 }))}
            />
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
              Minimum 5s. At this pace, {selected.size} devices will take about{' '}
              {Math.round((selected.size - 1) * bulkModal.paceSeconds / 60) || '<1'} minute(s) to fully dispatch.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setBulkModal(null)}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => bulkMutation.mutate()}
                disabled={
                  bulkMutation.isPending ||
                  (bulkModal.mode === 'update' && bulkModal.updateType === 'agent' && !bulkModal.releaseVersion) ||
                  (bulkModal.mode === 'update' && bulkModal.updateType === 'package' && !bulkModal.packageSpec.trim())
                }
                style={{
                  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {bulkMutation.isPending ? 'Starting…' : `${bulkModal.mode === 'reboot' ? 'Reboot' : 'Push Update to'} ${selected.size} Device${selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkOps.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Recent Bulk Operations</h3>
          <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Started</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>Progress</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {bulkOps.map(op => (
                  <tr key={op.ID}>
                    <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(op.CreatedAt).toLocaleString()}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{op.UpdateType}</td>
                    <td style={tdStyle}>{op.CreatedByEmail}</td>
                    <td style={tdStyle}>{op.NextItemIndex} / {op.Items.length}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
                        background: op.Status === 'running' ? '#dbeafe' : op.Status === 'completed' ? '#d1fae5' : '#fee2e2',
                        color: op.Status === 'running' ? '#1e40af' : op.Status === 'completed' ? '#065f46' : '#991b1b',
                      }}>
                        {op.Status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {op.Status === 'running' && (
                        <button
                          style={{ ...bulkBtnStyle, background: '#fee2e2', color: '#991b1b', border: 'none' }}
                          onClick={() => cancelBulkMutation.mutate(op.ID)}
                          disabled={cancelBulkMutation.isPending}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

const bulkBtnStyle: React.CSSProperties = {
  padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: 6,
  padding: '8px 12px', fontSize: 14,
}
